/**
 * Clés de cache centralisées.
 *
 * Les définir ici (plutôt que d'écrire des tableaux littéraux dans chaque hook)
 * rend l'invalidation fiable : `queryClient.invalidateQueries({ queryKey:
 * queryKeys.candidates.all })` invalide listes et détails d'un seul coup, sans
 * risque de faute de frappe dans une clé.
 *
 * Convention : `all` -> `lists()` -> `list(filtres)` et `details()` -> `detail(id)`.
 */
export const queryKeys = {
  dashboard: {
    all: ['dashboard'] as const,
    section: (filters: object) => [...queryKeys.dashboard.all, filters] as const,
    owners: () => [...queryKeys.dashboard.all, 'owners'] as const,
  },
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  companies: {
    all: ['companies'] as const,
    lists: () => [...queryKeys.companies.all, 'list'] as const,
    /**
     * Les filtres font partie de la clé : chaque combinaison a son cache.
     *
     * Le paramètre est typé `object` et non `Record<string, unknown>` : une
     * interface déclarée (`CompanyFilters`) n'a pas de signature d'index et
     * serait refusée par le second, alors qu'elle convient parfaitement ici.
     */
    list: (filters: object) => [...queryKeys.companies.lists(), filters] as const,
    details: () => [...queryKeys.companies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.companies.details(), id] as const,
  },

  contacts: {
    all: ['contacts'] as const,
    lists: () => [...queryKeys.contacts.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.contacts.lists(), filters] as const,
    /**
     * Contacts d'une société donnée.
     *
     * Clé distincte de `list` : c'est elle qui permet au cache de conserver
     * séparément les contacts de chaque société sélectionnée, et donc de
     * réafficher instantanément une société déjà consultée.
     */
    byCompany: (companyId: string, filters: object) =>
      [...queryKeys.contacts.all, 'by-company', companyId, filters] as const,
    /**
     * Choix d'un menu déroulant, par société.
     *
     * Distincte de `list` : la route `/contacts/options` ne pagine pas et ne
     * rend que de quoi afficher un choix. Partager la clé de la liste ferait
     * lire l'un pour l'autre.
     */
    options: (companyId: string | undefined) =>
      [...queryKeys.contacts.all, 'options', companyId ?? 'all'] as const,
    details: () => [...queryKeys.contacts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contacts.details(), id] as const,
  },

  candidates: {
    all: ['candidates'] as const,
    lists: () => [...queryKeys.candidates.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.candidates.lists(), filters] as const,
    details: () => [...queryKeys.candidates.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.candidates.details(), id] as const,
  },

  /**
   * Catalogue de compétences du scrapper, relayé par notre API.
   *
   * Clé séparée de `skills`, qui interroge notre miroir : ces deux listes
   * répondent à deux questions et coûtent deux choses différentes — l’une lit
   * Postgres, l’autre sort du réseau. Un terme par entrée de cache, comme pour
   * `skills` : le catalogue se cherche, il ne s’énumère pas.
   */
  referential: {
    all: ['referential'] as const,
    search: (term: string, page: number) =>
      [...queryKeys.referential.all, 'search', term, page] as const,
  },

  requirements: {
    all: ['requirements'] as const,
    lists: () => [...queryKeys.requirements.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.requirements.lists(), filters] as const,
    details: () => [...queryKeys.requirements.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.requirements.details(), id] as const,
  },

  adv: {
    all: ['adv'] as const,
    documents: () => [...queryKeys.adv.all, 'documents'] as const,
    documentList: (filters: object) => [...queryKeys.adv.documents(), 'list', filters] as const,
    document: (id: string) => [...queryKeys.adv.documents(), 'detail', id] as const,
    missions: () => [...queryKeys.adv.all, 'missions'] as const,
    missionList: (filters: object) => [...queryKeys.adv.missions(), 'list', filters] as const,
    mission: (id: string) => [...queryKeys.adv.missions(), 'detail', id] as const,
    /**
     * Statuts permis par type.
     *
     * Clé distincte des listes : ces règles ne changent qu'à l'administration
     * d'un référentiel, alors que le journal bouge à chaque saisie.
     */
    options: (type: string) => [...queryKeys.adv.all, 'options', type] as const,
  },

  /**
   * Prospection.
   *
   * Séparée des contacts : la même personne y est lue sous un autre angle —
   * avec son dernier échange et sa prochaine relance — et une action
   * enregistrée doit rafraîchir ce portefeuille sans toucher au répertoire.
   */
  prospection: {
    all: ['prospection'] as const,
    lists: () => [...queryKeys.prospection.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.prospection.lists(), filters] as const,
  },

  /**
   * Comptes rendus d'activité.
   *
   * `access` est à part : l'accès au portail appartient au candidat, pas au
   * CRA, et il survit à toutes les déclarations.
   */
  timesheets: {
    all: ['timesheets'] as const,
    lists: () => [...queryKeys.timesheets.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.timesheets.lists(), filters] as const,
    details: () => [...queryKeys.timesheets.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.timesheets.details(), id] as const,
    options: () => [...queryKeys.timesheets.all, 'options'] as const,
    access: (candidateId: string) => [...queryKeys.timesheets.all, 'access', candidateId] as const,
  },

  leaveRequests: {
    all: ['leave-requests'] as const,
    list: (filters: object) => [...queryKeys.leaveRequests.all, filters] as const,
  },

  actions: {
    all: ['actions'] as const,
    lists: () => [...queryKeys.actions.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.actions.lists(), filters] as const,
    details: () => [...queryKeys.actions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.actions.details(), id] as const,
    /**
     * Options de saisie, par périmètre.
     *
     * Clé distincte des listes : ces règles ne changent qu'à l'administration
     * d'un référentiel, alors que le journal bouge à chaque saisie.
     */
    options: (scope: string) => [...queryKeys.actions.all, 'options', scope] as const,
  },

  users: {
    all: ['users'] as const,
    list: () => [...queryKeys.users.all, 'list'] as const,
  },

  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.projects.lists(), filters] as const,
    details: () => [...queryKeys.projects.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.projects.details(), id] as const,
  },

  cvImports: {
    all: ['cv-imports'] as const,
    details: () => [...queryKeys.cvImports.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.cvImports.details(), id] as const,
  },

  requirementImports: {
    all: ['requirement-imports'] as const,
    details: () => [...queryKeys.requirementImports.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.requirementImports.details(), id] as const,
  },

  /**
   * Rapprochements candidat/besoin.
   *
   * Clés séparées par sens : les deux listes se calculent différemment et
   * s'invalident pour des raisons différentes — modifier les technologies d'un
   * besoin périme ses candidats, pas les besoins d'un candidat.
   */
  matching: {
    all: ['matching'] as const,
    candidatesFor: (requirementId: string, limit: number) =>
      [...queryKeys.matching.all, 'candidates', requirementId, limit] as const,
    requirementsFor: (candidateId: string, limit: number) =>
      [...queryKeys.matching.all, 'requirements', candidateId, limit] as const,
  },

  /**
   * Séquences de messages et leurs lancements.
   *
   * Deux arbres distincts sous la même racine : une séquence se modifie, un
   * lancement est figé. Les invalider ensemble rechargerait toute la liste des
   * lancements à chaque virgule corrigée dans un modèle de message.
   */
  messaging: {
    all: ['messaging'] as const,
    lists: () => [...queryKeys.messaging.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.messaging.lists(), filters] as const,
    details: () => [...queryKeys.messaging.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.messaging.details(), id] as const,
    runs: () => [...queryKeys.messaging.all, 'runs'] as const,
    runList: (filters: object) => [...queryKeys.messaging.runs(), 'list', filters] as const,
    run: (id: string) => [...queryKeys.messaging.runs(), id] as const,
    enrollments: (runId: string, filters: object) =>
      [...queryKeys.messaging.run(runId), 'enrollments', filters] as const,
  },

  reference: {
    all: ['reference'] as const,
    list: (name: string) => [...queryKeys.reference.all, name] as const,
  },

  /**
   * Miroir des compétences, qui tient lieu de référentiel des technologies.
   *
   * Clé à part des autres référentiels : il ne se charge pas en entier mais se
   * cherche au fil de la frappe, et chaque terme a donc son propre cache.
   */
  skills: {
    all: ['skills'] as const,
    search: (term: string) => [...queryKeys.skills.all, 'search', term] as const,
  },
} as const;
